import React, { Component } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Swal from "sweetalert2";
import CryptoJS from "crypto-js";

import withReactContent from "sweetalert2-react-content";
import {
  TrashIcon,
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const MySwal = withReactContent(
  Swal.mixin({
    confirmButtonColor: "#075985", // sky-800
  })
);

const API_BASE = process.env.REACT_APP_API_ENDPOINT;

class MHCPTool extends Component {
  constructor(props) {
    super(props);

    this.state = {

      // ===== SERVER CHECK (access gate) =====
      accessLoading: true,
      accessError: "",
      accessAllowed: false,
      pageId: "",
      leadEmail: "",

        // doc open state
        hasDocOpen: false,
        openedFileName: "",

        // password modal state
        passwordDialogOpen: false,
        passwordDialogTitle: "",
        passwordDialogMessage: "",
        passwordDialogOkText: "OK",
        passwordDialogCancelText: "Cancel",
        passwordDialogValue: "",
        passwordDialogError: "",

      // Main tabs
      activeTab: "profile", // profile | form | action | progress | preview

      // Form subtabs
      formTab: "presenting",
      isEditing: false,
      draft: null,
      saved: false,
      // =========================
      // PROFILE (Patient details)
      // =========================
      patientIsEditing: false,
      patientSaved: false,

      patientLastName: "",
      patientFirstName: "",
      patientDob: "", // YYYY-MM-DD
      patientSex: "", // male | female
      patientPronoun: "",

      patientAddressLine1: "",
      patientAddressLine2: "",
      patientSuburb: "",
      patientState: "",
      patientPostcode: "",
      patientMedicareNumber: "",
      // =========================
      // FORM FIELDS
      // =========================
      presentingIssue: "",
      history: "",
      examination: "",
      investigation: "",
      mentalStateExamination: "",
      risk: "",
      tools: "",
      diagnosis: "",

      pastHistory: "",
      childhoodHistory: "",
      familyHistory: "",
      occupation: "",
      socialHistory: "",
      substanceUseHistory: "",
      allergy: "",
      treatmentHistory: "",
      medicationHistory: "",

      predisposingFactors: "",
      precipitatingFactors: "",
      perpetuatingFactors: "",
      protectiveFactors: "",

      reasonToLive: "",
      canDoToKeepSafe: "",
      whereToSeekHelp: "",
      whoToCall: "",
      whoToTalkTo: "",
      professionalHelpToSeek: "",

      triggers: "",
      warningSigns: "",
      copingStrategies: "",
      supports: "",
      emergencyContacts: "",

      // =========================
      // ACTION PLAN (Kanban)
      // =========================
      actionGoals: [
        {
          id: "g1",
          title: "SMART goal: Improve sleep to 7 hours/night within 4 weeks",
          isEditingTitle: false,
          titleDraft: "",
          tasks: {
            todo: [
              {
                id: "t-seed-1",
                text: "Keep a consistent bedtime for 7 days",
                isEditing: false,
                draftText: "",
              },
            ],
            doing: [],
            done: [],
          },
          inputs: {
            todo: "",
            doing: "",
            done: "",
          },
        },
      ],

      // =========================
      // PROGRESS TAB
      // =========================
      progressEntries: [
        // Example seed
        // { id: "p1", dateISO: "2026-01-24", text: "Reviewed MHCP. Sleep improved..." }
      ],
      selectedProgressId: null, // which entry is selected
      progressText: "", // right textarea content
      progressMode: "new", // new | view | edit
      progressDraftText: "", // used during edit mode to allow cancel
    };

    this.pwInputRef = React.createRef(); 
    
  }

  componentDidMount() {
    this.checkAccessOnServer();
  }


  /* =========================
    PAGE CHECKER
  ========================= */
  getQueryParam = (key) => {
    try {
      const sp = new URLSearchParams(window.location.search || "");
      return sp.get(key) || "";
    } catch {
      return "";
    }
  };

  checkAccessOnServer = async () => {
    const pageId = this.getQueryParam("id");

    // No id = no access
    if (!pageId) {
      this.setState({
        accessLoading: false,
        accessAllowed: false,
        accessError: "Missing link ID. Please open the tool from your email link.",
        pageId: "",
      });
      return;
    }

    this.setState({ accessLoading: true, accessError: "", pageId });

    try {
      // IMPORTANT: include credentials so cookies are sent/received
      const r = await fetch(
        `${API_BASE}/api/magic/page/visit?id=${encodeURIComponent(pageId)}`,
        { method: "GET", credentials: "include" }
      );

      const data = await r.json().catch(() => ({}));

      if (!r.ok || !data || data.ok === false) {
        this.setState({
          accessLoading: false,
          accessAllowed: false,
          accessError: data?.error || "Invalid or expired link.",
        });
        return;
      }

      // Decide your gate rule:
      // Option A (strict): must be email verified
      const allowed = !!data.isEmailVerified;

      if (!allowed) {
        this.setState({
          accessLoading: false,
          accessAllowed: false,
          accessError:
            "Your email is not verified yet. Please click the verification link in your email, then refresh this page.",
          leadEmail: data.email || "",
        });
        return;
      }

      // Allowed
      this.setState({
        accessLoading: false,
        accessAllowed: true,
        accessError: "",
        leadEmail: data.email || "",
      });

      // Optional: auto-open a blank doc when allowed
      // this.handleNew();

    } catch (err) {
      console.error("checkAccessOnServer failed:", err);
      this.setState({
        accessLoading: false,
        accessAllowed: false,
        accessError: "Network error. Please refresh and try again.",
      });
    }
  };


  /* =========================
     HELPERS
  ========================= */
  makeId = (prefix = "id") =>
    `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  formatDDMMMYYYY = (dateISO) => {
    // dateISO expected YYYY-MM-DD
    if (!dateISO) return "—";
    const [y, m, d] = dateISO.split("-").map((x) => parseInt(x, 10));
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const mm = months[(m || 1) - 1] || "Jan";
    const dd = String(d || 1).padStart(2, "0");
    return `${dd}-${mm}-${y}`;
  };

  todayISO = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  /* =========================
     FILE HANDLING
  ========================= */

  getSuggestedFilename = () => {
    const fn = (this.state.patientFirstName || "").trim();
    const ln = (this.state.patientLastName || "").trim();
    const dob = (this.state.patientDob || "").trim(); // YYYY-MM-DD

    const base = [fn, ln, dob].filter(Boolean).join("_") || "JohnDoe";
    const safe = base.replace(/[^\w\-]+/g, "_");
    return `${safe}.cdv`;
  };

  serializeState = () => {
    const { fileHandle, ...rest } = this.state; // if you later store a fileHandle, drop it here
    return JSON.stringify(rest);
  };

  showPasswordDialog = ({
    title = "Enter Password",
    message = "",
    okText = "OK",
    cancelText = "Cancel",
    defaultValue = "",
    validate,
  } = {}) => {
    if (this._pwReject) {
      try { this._pwReject(new Error("Superseded")); } catch {}
    }

    return new Promise((resolve, reject) => {
      this._pwResolve = resolve;
      this._pwReject = reject;
      this.setState(
        {
          passwordDialogOpen: true,
          passwordDialogTitle: title,
          passwordDialogMessage: message,
          passwordDialogOkText: okText,
          passwordDialogCancelText: cancelText,
          passwordDialogValue: defaultValue,
          passwordDialogError: "",
        },
        () => setTimeout(() => this.pwInputRef.current?.focus(), 0)
      );

      this._confirmPasswordDialog = () => {
        const v = (this.state.passwordDialogValue || "").trim();
        if (!v) {
          this.setState({ passwordDialogError: "Password cannot be empty." });
          return;
        }
        if (typeof validate === "function") {
          const err = validate(v);
          if (err) {
            this.setState({ passwordDialogError: err });
            return;
          }
        }
        this.setState({ passwordDialogOpen: false }, () => {
          this._pwResolve?.(v);
          this._pwResolve = null;
          this._pwReject = null;
        });
      };

      this._cancelPasswordDialog = () => {
        this.setState({ passwordDialogOpen: false }, () => {
          this._pwResolve?.(null);
          this._pwResolve = null;
          this._pwReject = null;
        });
      };
    });
  };

  encryptState = (passphrase) => {
    const plaintext = this.serializeState();
    return CryptoJS.AES.encrypt(plaintext, passphrase).toString();
  };

  decryptState = (ciphertext, passphrase) => {
    const bytes = CryptoJS.AES.decrypt(ciphertext, passphrase);
    const json = bytes.toString(CryptoJS.enc.Utf8);
    if (!json) throw new Error("Decryption failed. Wrong password or corrupt file.");
    return JSON.parse(json);
  };

  handleNew = () => {
    const blank = this.getBlankState();
    this.setState({
      ...blank,
      hasDocOpen: true,
      openedFileName: "",
    });
  };

  handleClose = () => {
    const blank = this.getBlankState();
    this.setState({
      ...blank,
      hasDocOpen: false,
      openedFileName: "",
    });
  };

  saveEncryptedCDV = async () => {
    try {
      const pass = await this.showPasswordDialog({
        title: "Encrypt & Save",
        message: "Enter a password to encrypt this file.",
        okText: "Encrypt & Save",
      });
      if (pass == null) return;

      const cipher = this.encryptState(pass);
      const suggestedName = this.state.openedFileName || this.getSuggestedFilename();

      // File System Access API
      if (window.showSaveFilePicker) {
        const handle = await window.showSaveFilePicker({
          suggestedName,
          types: [
            {
              description: "Caredevo Encrypted File",
              accept: { "application/x-caredevo": [".cdv"] },
            },
          ],
          excludeAcceptAllOption: false,
        });

        const writable = await handle.createWritable();
        await writable.write(cipher);
        await writable.close();

        this.setState({ openedFileName: suggestedName, saved: true });
        return;
      }

      // Fallback download
      const blob = new Blob([cipher], { type: "application/x-caredevo" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = suggestedName;
      a.click();
      URL.revokeObjectURL(url);

      this.setState({ openedFileName: suggestedName, saved: true });
    } catch (err) {
      console.error(err);
      window.alert(`Save failed: ${err.message || err}`);
    }
  };

  loadEncryptedCDV = async () => {
    try {
      // File System Access API
      if (window.showOpenFilePicker) {
        const [handle] = await window.showOpenFilePicker({
          multiple: false,
          types: [
            {
              description: "Caredevo Encrypted File",
              accept: { "application/x-caredevo": [".cdv"] },
            },
          ],
          excludeAcceptAllOption: false,
        });

        const file = await handle.getFile();
        const text = await file.text();

        const pass = await this.showPasswordDialog({
          title: "Open Encrypted File",
          message: "Enter the password to decrypt.",
          okText: "Decrypt & Open",
        });
        if (pass == null) return;

        const data = this.decryptState(text, pass);

        this.setState({
          ...this.getBlankState(),
          ...data,
          hasDocOpen: true,
          openedFileName: file.name || "",
          saved: true,
        });
        return;
      }

      // Fallback input for Safari
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".cdv,application/x-caredevo";
      input.onchange = async () => {
        const file = input.files && input.files[0];
        if (!file) return;

        const text = await file.text();
        const pass = await this.showPasswordDialog({
          title: "Open Encrypted File",
          message: "Enter the password to decrypt.",
          okText: "Decrypt & Open",
        });
        if (pass == null) return;

        const data = this.decryptState(text, pass);

        this.setState({
          ...this.getBlankState(),
          ...data,
          hasDocOpen: true,
          openedFileName: file.name || "",
          saved: true,
        });
      };
      input.click();
    } catch (err) {
      console.error(err);
      window.alert(`Fail loading file: ${err.message || err}`);
    }
  };







  getBlankState = () => ({
    activeTab: "profile",
    formTab: "presenting",
    isEditing: false,
    draft: null,
    saved: false,

    patientIsEditing: false,
    patientSaved: false,

    patientLastName: "",
    patientFirstName: "",
    patientDob: "",
    patientSex: "",
    patientPronoun: "",
    patientAddressLine1: "",
    patientAddressLine2: "",
    patientSuburb: "",
    patientState: "",
    patientPostcode: "",
    patientMedicareNumber: "",

    presentingIssue: "",
    history: "",
    examination: "",
    investigation: "",
    mentalStateExamination: "",
    risk: "",
    tools: "",
    diagnosis: "",

    pastHistory: "",
    childhoodHistory: "",
    familyHistory: "",
    occupation: "",
    socialHistory: "",
    substanceUseHistory: "",
    allergy: "",
    treatmentHistory: "",
    medicationHistory: "",

    predisposingFactors: "",
    precipitatingFactors: "",
    perpetuatingFactors: "",
    protectiveFactors: "",

    reasonToLive: "",
    canDoToKeepSafe: "",
    whereToSeekHelp: "",
    whoToCall: "",
    whoToTalkTo: "",
    professionalHelpToSeek: "",

    triggers: "",
    warningSigns: "",
    copingStrategies: "",
    supports: "",
    emergencyContacts: "",

    actionGoals: [],
    progressEntries: [],
    selectedProgressId: null,
    progressText: "",
    progressMode: "new",
    progressDraftText: "",
  });


  /* =========================
     MAIN TABS
  ========================= */
  setMainTab = (tab) => {
    this.setState({ activeTab: tab });
  };

  renderMainTabs() {
    const { activeTab } = this.state;

    const tabClass = (tab) =>
      `px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer ${
        activeTab === tab
          ? "bg-sky-700 text-white"
          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
      }`;

    return (
      <div className="flex flex-wrap gap-2 mb-6 print:hidden">

        <button
          type="button"
          onClick={() => this.setMainTab("profile")}
          className={tabClass("profile")}
        >
          Profile
        </button>

        <button
          type="button"
          onClick={() => this.setMainTab("form")}
          className={tabClass("form")}
        >
          Background
        </button>
        <button
          type="button"
          onClick={() => this.setMainTab("action")}
          className={tabClass("action")}
        >
          Action Plan
        </button>
        <button
          type="button"
          onClick={() => this.setMainTab("progress")}
          className={tabClass("progress")}
        >
          Progress
        </button>
        <button
          type="button"
          onClick={() => this.setMainTab("preview")}
          className={tabClass("preview")}
        >
          Preview
        </button>
      </div>
    );
  }

  togglePatientEdit = () => {
  this.setState((prev) => {
    const nextIsEditing = !prev.patientIsEditing;
    return {
      patientIsEditing: nextIsEditing,
      patientSaved: prev.patientIsEditing ? true : false, // saving when leaving edit
    };
  });
};

handlePatientChange = (e) => {
  const { name, value } = e.target;
  this.setState({ [name]: value, patientSaved: false });
};

renderPatientField = ({ name, label, placeholder, type = "text" }) => {
  const { patientIsEditing } = this.state;
  const value = this.state[name] || "";

  return (
    <div className="mb-4">
      <label className="block text-xs font-semibold text-slate-600 mb-1">
        {label}
      </label>

      {patientIsEditing ? (
        <input
          type={type}
          name={name}
          value={value}
          onChange={this.handlePatientChange}
          placeholder={placeholder || ""}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
        />
      ) : (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
          {value ? value : <span className="text-slate-400">—</span>}
        </div>
      )}
    </div>
  );
};

renderPatientSelect = ({ name, label, options }) => {
  const { patientIsEditing } = this.state;
  const value = this.state[name] || "";

  return (
    <div className="mb-4">
      <label className="block text-xs font-semibold text-slate-600 mb-1">
        {label}
      </label>

      {patientIsEditing ? (
        <select
          name={name}
          value={value}
          onChange={this.handlePatientChange}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
        >
          <option value="">Select…</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
          {value ? value : <span className="text-slate-400">—</span>}
        </div>
      )}
    </div>
  );
};

  /* =========================
     PROFILE
  ========================= */
  renderProfileTab() {
    const { patientIsEditing, patientSaved } = this.state;

    const sexOptions = [
      { value: "male", label: "Male" },
      { value: "female", label: "Female" },
    ];

    const pronounOptions = [
      { value: "she-her", label: "She / Her" },
      { value: "he-him", label: "He / Him" },
      { value: "they-them", label: "They / Them" },
      { value: "use-name", label: "Use my name" },
      { value: "prefer-not", label: "Prefer not to say" },
      { value: "other", label: "Other" },
    ];

    return (
      <div className="rounded-2xl bg-slate-50 p-6 ring-1 ring-slate-200">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-lg font-bold text-slate-900">Patient profile</div>
            <div className="text-sm text-slate-600 mt-1">
              These details will appear in Preview and printout.
            </div>
          </div>

          <button
            type="button"
            onClick={this.togglePatientEdit}
            className={[
              "rounded-lg px-4 py-2 text-sm font-semibold transition",
              patientIsEditing
                ? "bg-sky-700 text-white hover:bg-sky-600"
                : "bg-slate-900 text-white hover:bg-slate-800",
            ].join(" ")}
          >
            {patientIsEditing ? "Save" : "Edit"}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {this.renderPatientField({
            name: "patientLastName",
            label: "Last name",
            placeholder: "e.g. Smith",
          })}
          {this.renderPatientField({
            name: "patientFirstName",
            label: "First name",
            placeholder: "e.g. John",
          })}
          {this.renderPatientField({
            name: "patientDob",
            label: "Date of birth",
            type: "date",
          })}

          {this.renderPatientSelect({
            name: "patientSex",
            label: "Sex",
            options: sexOptions,
          })}
          {this.renderPatientSelect({
            name: "patientPronoun",
            label: "Pronoun",
            options: pronounOptions,
          })}
          {this.renderPatientField({
            name: "patientMedicareNumber",
            label: "Medicare number",
            placeholder: "e.g. 1234 56789 1",
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          {this.renderPatientField({
            name: "patientAddressLine1",
            label: "Address line 1",
            placeholder: "Street address",
          })}
          {this.renderPatientField({
            name: "patientAddressLine2",
            label: "Address line 2",
            placeholder: "Unit / apartment / c/o",
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
          {this.renderPatientField({
            name: "patientSuburb",
            label: "Suburb",
            placeholder: "e.g. Geelong",
          })}
          {this.renderPatientField({
            name: "patientPostcode",
            label: "Postcode",
            placeholder: "e.g. 3677",
          })}
          <div className="hidden md:block" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
          {this.renderPatientField({
            name: "patientState",
            label: "State",
            placeholder: "e.g. VIC",
          })}
          {this.renderPatientField({
            name: "patientPostcode",
            label: "Postcode",
            placeholder: "e.g. 3677",
          })}
          <div className="hidden md:block" />
        </div>

        {!patientIsEditing && patientSaved && (
          <div className="mt-2 text-sm text-emerald-600 font-medium">
            ✓ Saved 
          </div>
        )}
      </div>
    );
  }


  /* =========================
     FORM
  ========================= */
  setFormTab = (tab) => this.setState({ formTab: tab });

  startEdit = () => {
    const { activeTab, formTab, isEditing, draft, saved, actionGoals, ...data } =
      this.state;

    this.setState({
      isEditing: true,
      draft: { ...data },
      saved: false,
    });
  };

  cancelEdit = () => {
    const { draft } = this.state;
    if (!draft) return this.setState({ isEditing: false });

    this.setState({
      ...draft,
      isEditing: false,
      draft: null,
      saved: false,
    });
  };

  saveEdit = () => this.setState({ isEditing: false, draft: null, saved: true });

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value, saved: false });
  };

  renderTextarea = ({ name, label, placeholder, rows = 3 }) => {
    const { isEditing } = this.state;
    const value = this.state[name] || "";

    return (
      <div className="mb-5">
        <label className="block text-sm font-medium text-slate-700 mb-1">
          {label}
        </label>

        {isEditing ? (
          <textarea
            name={name}
            value={value}
            onChange={this.handleChange}
            rows={rows}
            placeholder={placeholder || ""}
            className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
        ) : (
          <div className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            {value ? value : <span className="text-slate-400">—</span>}
          </div>
        )}
      </div>
    );
  };

  renderSubtabs() {
    const { formTab } = this.state;

    const tabClass = (tab) =>
      `px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer ${
        formTab === tab
          ? "bg-sky-700 text-white"
          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
      }`;

    return (
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          type="button"
          onClick={() => this.setFormTab("presenting")}
          className={tabClass("presenting")}
        >
          Presenting issue
        </button>
        <button
          type="button"
          onClick={() => this.setFormTab("history")}
          className={tabClass("history")}
        >
          History
        </button>
        <button
          type="button"
          onClick={() => this.setFormTab("formulation")}
          className={tabClass("formulation")}
        >
          Formulation
        </button>
        <button
          type="button"
          onClick={() => this.setFormTab("safety")}
          className={tabClass("safety")}
        >
          Safety plan
        </button>
        <button
          type="button"
          onClick={() => this.setFormTab("relapse")}
          className={tabClass("relapse")}
        >
          Relapse prevention
        </button>
      </div>
    );
  }

  renderSectionTitle(title, subtitle) {
    return (
      <div className="mb-4">
        <h2 className="text-lg font-bold text-slate-900">{title}</h2>
        {subtitle ? (
          <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
        ) : null}
      </div>
    );
  }

  renderPresentingSection() {
    return (
      <>
        {this.renderSectionTitle(
          "Presenting issue",
          "Core presentation, assessment, and working diagnosis."
        )}

        {this.renderTextarea({
          name: "presentingIssue",
          label: "Presenting issue",
          placeholder: "Brief summary of presenting concerns…",
        })}
        {this.renderTextarea({
          name: "history",
          label: "History",
          placeholder: "Onset, duration, severity, course, triggers…",
        })}
        {this.renderTextarea({
          name: "examination",
          label: "Examination",
          placeholder: "Relevant physical exam and general observations…",
        })}
        {this.renderTextarea({
          name: "investigation",
          label: "Investigation",
          placeholder: "Relevant pathology, imaging, screening tools, results…",
        })}
        {this.renderTextarea({
          name: "mentalStateExamination",
          label: "Mental state examination",
          placeholder:
            "Appearance, behaviour, speech, mood/affect, thought, perception, cognition, insight…",
          rows: 4,
        })}
        {this.renderTextarea({
          name: "risk",
          label: "Risk",
          placeholder: "Self-harm/suicide, harm to others, vulnerability…",
        })}
        {this.renderTextarea({
          name: "tools",
          label: "Tools",
          placeholder: "e.g. K10, PHQ-9, GAD-7, PCL-5, AUDIT-C…",
        })}
        {this.renderTextarea({
          name: "diagnosis",
          label: "Diagnosis",
          placeholder: "Working diagnosis / differential diagnoses…",
        })}
      </>
    );
  }

  renderHistorySection() {
    return (
      <>
        {this.renderSectionTitle(
          "History",
          "Background history, context, treatments, and contributing factors."
        )}

        {this.renderTextarea({
          name: "pastHistory",
          label: "Past history",
          placeholder: "Medical + mental health history, prior episodes…",
        })}
        {this.renderTextarea({
          name: "childhoodHistory",
          label: "Childhood history",
          placeholder: "Development, trauma, schooling, attachment…",
        })}
        {this.renderTextarea({
          name: "familyHistory",
          label: "Family history",
          placeholder: "Mental health, substance use, medical conditions…",
        })}
        {this.renderTextarea({
          name: "occupation",
          label: "Occupation",
          placeholder: "Role, workload, capacity, stressors…",
        })}
        {this.renderTextarea({
          name: "socialHistory",
          label: "Social history",
          placeholder: "Living situation, supports, relationships, finances…",
        })}
        {this.renderTextarea({
          name: "substanceUseHistory",
          label: "Substance use history",
          placeholder: "Alcohol, nicotine, cannabis, other substances…",
        })}
        {this.renderTextarea({
          name: "allergy",
          label: "Allergy",
          placeholder: "Drug allergies and reactions…",
        })}
        {this.renderTextarea({
          name: "treatmentHistory",
          label: "Treatment history",
          placeholder: "Therapy, admissions, prior referrals…",
        })}
        {this.renderTextarea({
          name: "medicationHistory",
          label: "Medication history",
          placeholder: "Current + past psych meds, response, side effects…",
        })}
      </>
    );
  }

  renderFormulationSection() {
    return (
      <>
        {this.renderSectionTitle(
          "Formulation",
          "4Ps formulation (Predisposing, Precipitating, Perpetuating, Protective)."
        )}

        {this.renderTextarea({
          name: "predisposingFactors",
          label: "Predisposing factors",
          placeholder:
            "Vulnerabilities, long-term factors, developmental/biological…",
        })}
        {this.renderTextarea({
          name: "precipitatingFactors",
          label: "Precipitating factors",
          placeholder: "Recent triggers/events leading to presentation…",
        })}
        {this.renderTextarea({
          name: "perpetuatingFactors",
          label: "Perpetuating factors",
          placeholder:
            "Factors maintaining symptoms (sleep, pain, avoidance, substances, stress)…",
        })}
        {this.renderTextarea({
          name: "protectiveFactors",
          label: "Protective factors",
          placeholder: "Strengths, supports, motivation, values, coping skills…",
        })}
      </>
    );
  }

  renderSafetySection() {
    return (
      <>
        {this.renderSectionTitle(
          "Safety plan",
          "Clear, practical steps for staying safe and getting help."
        )}

        {this.renderTextarea({
          name: "reasonToLive",
          label: "Reason to live",
          placeholder: "What matters most / anchors / responsibilities…",
        })}
        {this.renderTextarea({
          name: "canDoToKeepSafe",
          label: "Can do to keep safe",
          placeholder:
            "Immediate steps: safe environment, remove means, grounding, distraction…",
        })}
        {this.renderTextarea({
          name: "whereToSeekHelp",
          label: "Where to seek help",
          placeholder: "ED, crisis service, clinic, safe places…",
        })}
        {this.renderTextarea({
          name: "whoToCall",
          label: "Who to call",
          placeholder: "Key people to call in a crisis…",
        })}
        {this.renderTextarea({
          name: "whoToTalkTo",
          label: "Who to talk to",
          placeholder: "Trusted people for emotional support…",
        })}
        {this.renderTextarea({
          name: "professionalHelpToSeek",
          label: "Professional help to seek",
          placeholder: "GP, psychologist, psychiatrist, helplines, case worker…",
        })}
      </>
    );
  }

  renderRelapseSection() {
    return (
      <>
        {this.renderSectionTitle(
          "Relapse prevention plan",
          "Plan for early detection and action if symptoms return or worsen."
        )}

        {this.renderTextarea({
          name: "triggers",
          label: "Triggers",
          placeholder: "Situations/stressors that commonly trigger symptoms…",
        })}
        {this.renderTextarea({
          name: "warningSigns",
          label: "Warning signs",
          placeholder: "Early signs: sleep change, withdrawal, irritability…",
        })}
        {this.renderTextarea({
          name: "copingStrategies",
          label: "Coping strategies",
          placeholder: "What helps: routines, activity, grounding, exercise…",
        })}
        {this.renderTextarea({
          name: "supports",
          label: "Supports",
          placeholder: "People/services that help keep things on track…",
        })}
        {this.renderTextarea({
          name: "emergencyContacts",
          label: "Emergency contacts",
          placeholder: "Emergency numbers / crisis lines / local ED…",
        })}
      </>
    );
  }

  renderCurrentFormSection() {
    const { formTab } = this.state;
    if (formTab === "presenting") return this.renderPresentingSection();
    if (formTab === "history") return this.renderHistorySection();
    if (formTab === "formulation") return this.renderFormulationSection();
    if (formTab === "safety") return this.renderSafetySection();
    return this.renderRelapseSection();
  }

  renderEditBar() {
    const { isEditing } = this.state;

    if (!isEditing) {
      return (
        <div className="flex items-center justify-end mb-4">
          <button
            type="button"
            onClick={this.startEdit}
            className="rounded-lg bg-slate-900 px-4 py-2 text-white text-sm font-semibold hover:bg-slate-800 transition"
          >
            Edit
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-end gap-2 mb-4">
        <button
          type="button"
          onClick={this.cancelEdit}
          className="rounded-lg bg-slate-100 px-4 py-2 text-slate-800 text-sm font-semibold hover:bg-slate-200 transition"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={this.saveEdit}
          className="rounded-lg bg-sky-700 px-4 py-2 text-white text-sm font-semibold hover:bg-sky-600 transition"
        >
          Save
        </button>
      </div>
    );
  }

  renderFormTab() {
    const { saved, isEditing } = this.state;

    return (
      <>
        {this.renderSubtabs()}
        {this.renderEditBar()}

        <div className="rounded-2xl bg-slate-50 p-6 ring-1 ring-slate-200">
          {this.renderCurrentFormSection()}
        </div>

        {!isEditing && saved && (
          <div className="mt-4 text-sm text-emerald-600 font-medium">
            ✓ Saved (demo)
          </div>
        )}
      </>
    );
  }

  /* =========================
     GOALS: title edit + delete
  ========================= */

    addGoal = async () => {
    // Optional: prompt for goal title
    const res = await MySwal.fire({
      title: "New goal",
      input: "text",
      inputLabel: "Write new SMART goal below",
      inputPlaceholder: "e.g. No nightmare symptoms within 3 months",
      showCancelButton: true,
      confirmButtonText: "Add",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      inputValidator: (value) => {
        if (!value || !value.trim()) return "Please enter a goal title.";
        return null;
      },
    });

    if (!res.isConfirmed) return;

    const title = (res.value || "").trim();

    const newGoal = {
      id: this.makeId("g"),
      title,
      isEditingTitle: false,
      titleDraft: "",
      tasks: { todo: [], doing: [], done: [] },
      inputs: { todo: "", doing: "", done: "" },
    };

    this.setState((prev) => ({
      actionGoals: [...(prev.actionGoals || []), newGoal],
    }));
  };

  toggleEditGoalTitle = (goalId, shouldEdit) => {
    this.setState((prev) => ({
      actionGoals: prev.actionGoals.map((g) => {
        if (g.id !== goalId) return g;
        return {
          ...g,
          isEditingTitle: shouldEdit,
          titleDraft: shouldEdit ? (g.titleDraft || g.title) : g.titleDraft,
        };
      }),
    }));
  };

  changeGoalTitleDraft = (goalId, value) => {
    this.setState((prev) => ({
      actionGoals: prev.actionGoals.map((g) =>
        g.id === goalId ? { ...g, titleDraft: value } : g
      ),
    }));
  };

  saveGoalTitle = (goalId) => {
    this.setState((prev) => ({
      actionGoals: prev.actionGoals.map((g) => {
        if (g.id !== goalId) return g;
        const nextTitle = (g.titleDraft || "").trim();
        return {
          ...g,
          title: nextTitle ? nextTitle : g.title,
          isEditingTitle: false,
        };
      }),
    }));
  };

  confirmDeleteGoal = async (goalId) => {
    const res = await MySwal.fire({
      title: "Delete this goal?",
      text: "All tasks in To do / Doing / Done will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!res.isConfirmed) return;

    this.setState((prev) => ({
      actionGoals: prev.actionGoals.filter((g) => g.id !== goalId),
    }));
  };

  /* =========================
     TASK: delete + edit (existing)
  ========================= */
  confirmDeleteTask = async (goalId, col, taskId) => {
    const res = await MySwal.fire({
      title: "Delete this task?",
      text: "This can’t be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!res.isConfirmed) return;

    this.setState((prev) => ({
      actionGoals: prev.actionGoals.map((g) => {
        if (g.id !== goalId) return g;
        return {
          ...g,
          tasks: {
            ...g.tasks,
            [col]: (g.tasks[col] || []).filter((t) => t.id !== taskId),
          },
        };
      }),
    }));
  };

  startEditTask = (goalId, col, taskId) => {
    this.setState((prev) => ({
      actionGoals: prev.actionGoals.map((g) => {
        if (g.id !== goalId) return g;
        const list = (g.tasks[col] || []).map((t) => {
          if (t.id !== taskId) return t;
          return { ...t, isEditing: true, draftText: t.text };
        });
        return { ...g, tasks: { ...g.tasks, [col]: list } };
      }),
    }));
  };

  changeTaskDraft = (goalId, col, taskId, value) => {
    this.setState((prev) => ({
      actionGoals: prev.actionGoals.map((g) => {
        if (g.id !== goalId) return g;
        const list = (g.tasks[col] || []).map((t) =>
          t.id === taskId ? { ...t, draftText: value } : t
        );
        return { ...g, tasks: { ...g.tasks, [col]: list } };
      }),
    }));
  };

  cancelEditTask = (goalId, col, taskId) => {
    this.setState((prev) => ({
      actionGoals: prev.actionGoals.map((g) => {
        if (g.id !== goalId) return g;
        const list = (g.tasks[col] || []).map((t) => {
          if (t.id !== taskId) return t;
          return { ...t, isEditing: false, draftText: "" };
        });
        return { ...g, tasks: { ...g.tasks, [col]: list } };
      }),
    }));
  };

  saveEditTask = (goalId, col, taskId) => {
    this.setState((prev) => ({
      actionGoals: prev.actionGoals.map((g) => {
        if (g.id !== goalId) return g;
        const list = (g.tasks[col] || []).map((t) => {
          if (t.id !== taskId) return t;
          const nextText = (t.draftText || "").trim();
          return {
            ...t,
            text: nextText ? nextText : t.text,
            isEditing: false,
            draftText: "",
          };
        });
        return { ...g, tasks: { ...g.tasks, [col]: list } };
      }),
    }));
  };

  changeTaskInput = (goalId, col, value) => {
    this.setState((prev) => ({
      actionGoals: prev.actionGoals.map((g) => {
        if (g.id !== goalId) return g;
        return { ...g, inputs: { ...g.inputs, [col]: value } };
      }),
    }));
  };

  addTask = (goalId, col) => {
    this.setState((prev) => ({
      actionGoals: prev.actionGoals.map((g) => {
        if (g.id !== goalId) return g;

        const text = (g.inputs[col] || "").trim();
        if (!text) return g;

        const task = {
          id: this.makeId("t"),
          text,
          isEditing: false,
          draftText: "",
        };

        return {
          ...g,
          tasks: { ...g.tasks, [col]: [...(g.tasks[col] || []), task] },

          inputs: { ...g.inputs, [col]: "" },
        };
      }),
    }));
  };

  parseDroppableId = (droppableId) => {
    const [goalId, col] = String(droppableId).split("::");
    return { goalId, col };
  };

  onDragEnd = (result) => {
    const { destination, source } = result;
    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const { goalId: fromGoalId, col: fromCol } = this.parseDroppableId(
      source.droppableId
    );
    const { goalId: toGoalId, col: toCol } = this.parseDroppableId(
      destination.droppableId
    );

    this.setState((prev) => {
      const goals = prev.actionGoals.map((g) => ({
        ...g,
        tasks: {
          todo: [...(g.tasks.todo || [])],
          doing: [...(g.tasks.doing || [])],
          done: [...(g.tasks.done || [])],
        },
      }));

      const fromGoal = goals.find((g) => g.id === fromGoalId);
      const toGoal = goals.find((g) => g.id === toGoalId);
      if (!fromGoal || !toGoal) return null;

      const fromList = fromGoal.tasks[fromCol] || [];
      const toList = toGoal.tasks[toCol] || [];

      const [moved] = fromList.splice(source.index, 1);
      toList.splice(destination.index, 0, moved);

      fromGoal.tasks[fromCol] = fromList;
      toGoal.tasks[toCol] = toList;

      return { actionGoals: goals };
    });
  };

  renderKanbanColumn(goal, colKey, label) {
    const items = goal.tasks[colKey] || [];
    const inputVal = goal.inputs[colKey] || "";
    const droppableId = `${goal.id}::${colKey}`;

    return (
      <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200 ">
        <div className="text-sm font-semibold text-slate-100 mb-3 text-center bg-slate-500 rounded-lg p-2">{label}</div>

        <Droppable droppableId={droppableId} type="TASK" direction="vertical">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={[
                "relative rounded-lg p-2 transition",
                "min-h-[30px]",
                snapshot.isDraggingOver ? "bg-sky-50" : "bg-transparent",
              ].join(" ")}
            >
              {items.length === 0 && !snapshot.isDraggingOver ? (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-slate-400">
                  Drop tasks here
                </div>
              ) : null}

              <div className="flex flex-col gap-2">
                {items.map((t, idx) => (
                  <Draggable key={t.id} draggableId={t.id} index={idx}>
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        className={[
                          "rounded-lg border px-3 py-2 text-sm transition",
                          dragSnapshot.isDragging
                            ? "border-sky-300 bg-white shadow"
                            : "border-slate-200 bg-slate-50",
                          "text-slate-700",
                        ].join(" ")}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div
                            {...dragProvided.dragHandleProps}
                            className="flex-1 cursor-grab active:cursor-grabbing"
                          >
                            {t.isEditing ? (
                              <textarea
                                value={t.draftText || ""}
                                onChange={(e) =>
                                  this.changeTaskDraft(
                                    goal.id,
                                    colKey,
                                    t.id,
                                    e.target.value
                                  )
                                }
                                rows={2}
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                              />
                            ) : (
                              <div className="whitespace-pre-wrap">{t.text}</div>
                            )}
                          </div>

                          <div className="flex items-center gap-2 pt-0.5">
                            {!t.isEditing ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() =>
                                    this.startEditTask(goal.id, colKey, t.id)
                                  }
                                  className="rounded-md p-1 hover:bg-slate-200"
                                  title="Edit"
                                >
                                  <PencilSquareIcon className="h-5 w-5 text-slate-700" />
                                </button>

                                <button
                                  type="button"
                                  onClick={() =>
                                    this.confirmDeleteTask(goal.id, colKey, t.id)
                                  }
                                  className="rounded-md p-1 hover:bg-slate-200"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-5 w-5 text-slate-700" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() =>
                                    this.saveEditTask(goal.id, colKey, t.id)
                                  }
                                  className="rounded-md p-1 hover:bg-emerald-100"
                                  title="Save"
                                >
                                  <CheckIcon className="h-5 w-5 text-emerald-700" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    this.cancelEditTask(goal.id, colKey, t.id)
                                  }
                                  className="rounded-md p-1 hover:bg-slate-200"
                                  title="Cancel"
                                >
                                  <XMarkIcon className="h-5 w-5 text-slate-700" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}

                {provided.placeholder}
              </div>
            </div>
          )}
        </Droppable>

        <textarea
          value={inputVal}
          onChange={(e) =>
            this.changeTaskInput(goal.id, colKey, e.target.value)
          }
          rows={2}
          placeholder="Write task here..."
          className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
        />

        <button
          type="button"
          onClick={() => this.addTask(goal.id, colKey)}
          className="mt-2 w-full rounded-lg bg-sky-700 px-3 py-2 text-white text-sm font-semibold hover:bg-sky-600 transition"
        >
          Submit
        </button>
      </div>
    );
  }

  renderGoalCard(goal) {
    return (
      <div
        key={goal.id}
        className="rounded-2xl bg-slate-50 p-6 ring-1 ring-slate-200"
      >
        <div className="flex items-start justify-between gap-3 mb-5">
          <div className="flex-1">
            {goal.isEditingTitle ? (
              <>
                <input
                  value={goal.titleDraft || ""}
                  onChange={(e) =>
                    this.changeGoalTitleDraft(goal.id, e.target.value)
                  }
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />

                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => this.toggleEditGoalTitle(goal.id, false)}
                    className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-2 text-slate-800 text-xs font-semibold hover:bg-slate-200 transition"
                  >
                    <XMarkIcon className="h-4 w-4" />
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={() => this.saveGoalTitle(goal.id)}
                    className="inline-flex items-center gap-1 rounded-lg bg-sky-700 px-3 py-2 text-white text-xs font-semibold hover:bg-sky-600 transition"
                  >
                    <CheckIcon className="h-4 w-4" />
                    Save
                  </button>
                </div>
              </>
            ) : (
              <div className="text-base font-bold text-slate-900">
                {goal.title}
              </div>
            )}
          </div>

          {!goal.isEditingTitle && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => this.toggleEditGoalTitle(goal.id, true)}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-white text-xs font-semibold hover:bg-slate-700 transition"
                title="Edit goal"
              >
                <PencilSquareIcon className="h-4 w-4" />
                Edit
              </button>

              <button
                type="button"
                onClick={() => this.confirmDeleteGoal(goal.id)}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-slate-800 text-xs font-semibold hover:bg-slate-200 transition"
                title="Delete goal"
              >
                <TrashIcon className="h-4 w-4" />
                Delete
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 md:grid-cols-3 sm:grid-cols-1 xs:grid-cols-1 gap-4">
          {this.renderKanbanColumn(goal, "todo", "To do")}
          {this.renderKanbanColumn(goal, "doing", "Doing")}
          {this.renderKanbanColumn(goal, "done", "Done")}
        </div>
      </div>
    );
  }

  renderActionPlanTab() {
    const { actionGoals } = this.state;

    return (
      <>
        <div className="grid grid-cols-2 mb-5">
          <div>
            <div className="text-lg font-bold text-slate-900">Action plan</div>
            <div className="text-sm text-slate-600">
              Drag tasks across columns and across goals. Edit or delete tasks.
            </div>
          </div>

          <button
            type="button"
            onClick={this.addGoal}
            className="place-self-end rounded-lg bg-sky-700 px-4 py-2 text-white text-sm font-semibold hover:bg-sky-600 transition"
          >
            + Add Goal
          </button>
        </div>

        <DragDropContext onDragEnd={this.onDragEnd}>
          <div className="space-y-5">
            {actionGoals.map((g) => this.renderGoalCard(g))}
          </div>
        </DragDropContext>
      </>
    );
  }

  /* =========================
     PREVIEW PRINT
  ========================= */
  // printPreview = () => {
  //   const el = document.getElementById("mhcp-a4");
  //   if (!el) return;

  //   const w = window.open("", "_blank");
  //   if (!w) return;

  //   const html = el.outerHTML;

  //   w.document.open();
  //   w.document.write(`
  //     <!doctype html>
  //     <html>
  //       <head>
  //         <meta charset="utf-8" />
  //         <meta name="viewport" content="width=device-width, initial-scale=1" />
  //         <title>Mental Health Care Plan</title>

  //         <style>
  //           @page { size: A4; margin: 12mm; }
  //           html, body { margin: 0; padding: 0; }
  //           body {
  //             -webkit-print-color-adjust: exact;
  //             print-color-adjust: exact;
  //             font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
  //             color: #0f172a;
  //             background: #fff;
  //           }
  //           #mhcp-a4 {
  //             width: 210mm;
  //             min-height: 297mm;
  //             margin: 0 auto;
  //             background: #fff;
  //           }
  //           .shadow, .shadow-sm { box-shadow: none !important; }
  //         </style>
  //       </head>

  //       <body>
  //         ${html}
  //         <script>
  //           window.onload = function () {
  //             window.focus();
  //             window.print();
  //           };
  //         </script>
  //       </body>
  //     </html>
  //   `);
  //   w.document.close();
  // };

  printPreview = () => {
    window.print();
  }

  previewBlock = (label, value) => (
    <div className="mb-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="
          mt-1 whitespace-pre-wrap rounded-md border border-slate-200 bg-white 
          px-3 py-2 text-sm text-slate-800
          print:px-2 print:py-1 print:text-xs
        ">

        {value ? value : <span className="text-slate-400">—</span>}
      </div>
    </div>
  );

  previewSection = (title, children) => (
    <div className="mb-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-5 border-l-4 border-slate-900" />
        <div className="text-base font-bold text-slate-900">{title}</div>
      </div>
      <div className="h-px bg-slate-200 mb-3" />
      {children}
    </div>
  );

  renderPreviewTab() {
    const {
       patientLastName,
      patientFirstName,
      patientDob,
      patientSex,
      patientPronoun,
      patientMedicareNumber,
      patientAddressLine1,
      patientAddressLine2,
      patientSuburb,
      patientState,
      patientPostcode,

      presentingIssue,
      history,
      examination,
      investigation,
      mentalStateExamination,
      risk,
      tools,
      diagnosis,

      pastHistory,
      childhoodHistory,
      familyHistory,
      occupation,
      socialHistory,
      substanceUseHistory,
      allergy,
      treatmentHistory,
      medicationHistory,

      predisposingFactors,
      precipitatingFactors,
      perpetuatingFactors,
      protectiveFactors,

      reasonToLive,
      canDoToKeepSafe,
      whereToSeekHelp,
      whoToCall,
      whoToTalkTo,
      professionalHelpToSeek,

      triggers,
      warningSigns,
      copingStrategies,
      supports,
      emergencyContacts,

      actionGoals,
    } = this.state;

    return (

      <div className="">
        <div className="w-full">

          <div className="flex items-center justify-end mb-4 print:hidden">

            <button
              type="button"
              onClick={this.printPreview}
              className="rounded-lg bg-slate-900 px-4 py-2 text-white text-sm font-semibold hover:bg-slate-800 transition print:hidden"
            >
              Print
            </button>
          </div>

          <div
            className="bg-slate-100 p-2 rounded-xl w-full overflow-y-scroll print:p-0 print:bg-white print:rounded-none print:overflow-visible"
          >
           <div
              id="mhcp-a4"
              className="mx-auto bg-white shadow-sm border border-slate-200 p-[16mm] print:p-0 print:mx-0 print:scale-100 print:border-0 print:shadow-none"

              style={{
                width: "210mm",
                minHeight: "297mm",

              }}
>

              <div className="flex items-start justify-between gap-6 print:gap-0 ">
             
                
                  <div className="mt-4 text-3xl font-extrabold text-slate-900 text-center">
                    Mental Health Care Plan
                  </div>
                 
   

            
              </div>

              <div className="mt-5 h-px bg-slate-200" />
              <div className="mt-6 print-section">
              {this.previewSection("Patient profile", (
                <div className="grid grid-cols-2 gap-4">
                  {this.previewBlock(
                    "Patient",
                    [patientFirstName, patientLastName].filter(Boolean).join(" ") || ""
                  )}

                  {this.previewBlock("Date of birth", patientDob || "")}

                  {this.previewBlock("Sex", patientSex || "")}

                  {this.previewBlock("Pronoun", patientPronoun || "")}

                  {this.previewBlock("Medicare number", patientMedicareNumber || "")}

                  {this.previewBlock(
                    "Address",
                    [
                      patientAddressLine1,
                      patientAddressLine2,
                      [patientSuburb, patientState, patientPostcode].filter(Boolean).join(" "),
                    ]
                      .filter(Boolean)
                      .join("\n")
                  )}
                </div>
              ))}
            </div>

              <div className="mt-6 grid grid-cols-2 gap-6">
                <div>
                  {this.previewSection("Presenting issue", (
                    <>
                      {this.previewBlock("Presenting issue", presentingIssue)}
                      {this.previewBlock("History", history)}
                      {this.previewBlock("Examination", examination)}
                      {this.previewBlock("Investigation", investigation)}
                      {this.previewBlock(
                        "Mental state examination",
                        mentalStateExamination
                      )}
                      {this.previewBlock("Risk", risk)}
                      {this.previewBlock("Tools", tools)}
                      {this.previewBlock("Diagnosis", diagnosis)}
                    </>
                  ))}

                  {this.previewSection("Formulation (4Ps)", (
                    <>
                      {this.previewBlock(
                        "Predisposing factors",
                        predisposingFactors
                      )}
                      {this.previewBlock(
                        "Precipitating factors",
                        precipitatingFactors
                      )}
                      {this.previewBlock(
                        "Perpetuating factors",
                        perpetuatingFactors
                      )}
                      {this.previewBlock(
                        "Protective factors",
                        protectiveFactors
                      )}
                    </>
                  ))}
                </div>

                <div>
                  {this.previewSection("History", (
                    <>
                      {this.previewBlock("Past history", pastHistory)}
                      {this.previewBlock("Childhood history", childhoodHistory)}
                      {this.previewBlock("Family history", familyHistory)}
                      {this.previewBlock("Occupation", occupation)}
                      {this.previewBlock("Social history", socialHistory)}
                      {this.previewBlock(
                        "Substance use history",
                        substanceUseHistory
                      )}
                      {this.previewBlock("Allergy", allergy)}
                      {this.previewBlock("Treatment history", treatmentHistory)}
                      {this.previewBlock(
                        "Medication history",
                        medicationHistory
                      )}
                    </>
                  ))}

                  {this.previewSection("Safety plan", (
                    <>
                      {this.previewBlock("Reason to live", reasonToLive)}
                      {this.previewBlock(
                        "Can do to keep safe",
                        canDoToKeepSafe
                      )}
                      {this.previewBlock("Where to seek help", whereToSeekHelp)}
                      {this.previewBlock("Who to call", whoToCall)}
                      {this.previewBlock("Who to talk to", whoToTalkTo)}
                      {this.previewBlock(
                        "Professional help to seek",
                        professionalHelpToSeek
                      )}
                    </>
                  ))}

                  {this.previewSection("Relapse prevention plan", (
                    <>
                      {this.previewBlock("Triggers", triggers)}
                      {this.previewBlock("Warning signs", warningSigns)}
                      {this.previewBlock(
                        "Coping strategies",
                        copingStrategies
                      )}
                      {this.previewBlock("Supports", supports)}
                      {this.previewBlock(
                        "Emergency contacts",
                        emergencyContacts
                      )}
                    </>
                  ))}
                </div>
              </div>

              <div className="mt-2 page-break print-section">
                {this.previewSection("Action plan (SMART goals)", (
                  <>
                    {actionGoals.length === 0 ? (
                      <div className="text-sm text-slate-600">
                        No goals added yet.
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {actionGoals.map((g, idx) => (
                          <div
                            key={g.id}
                            className="rounded-lg border border-slate-200 bg-slate-50"
                          >
                            <div className="flex items-start justify-between gap-3 px-4 py-3">
                              <div>
                                <div className="text-xs text-slate-500">
                                  Goal {idx + 1}
                                </div>
                                <div className="text-sm font-bold text-slate-900">
                                  {g.title}
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-3 gap-0 border-t border-slate-200">
                              {[
                                { key: "todo", label: "To do" },
                                { key: "doing", label: "Doing" },
                                { key: "done", label: "Done" },
                              ].map((col, i) => (
                                <div
                                  key={col.key}
                                  className={[
                                    "px-4 py-3",
                                    i < 2 ? "border-r border-slate-200" : "",
                                  ].join(" ")}
                                >
                                  <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                                    {col.label}
                                  </div>
                                  <div className="mt-2 space-y-2">
                                    {(g.tasks[col.key] || []).length === 0 ? (
                                      <div className="text-xs text-slate-400">
                                        —
                                      </div>
                                    ) : (
                                      (g.tasks[col.key] || []).map((t) => (
                                        <div
                                          key={t.id}
                                          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800"
                                        >
                                          {t.text}
                                        </div>
                                      ))
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-6 text-[11px] text-slate-500">
                      Confidential clinical document • For professional use only
                    </div>
                  </>
                ))}
              </div>

              <div className="mt-8 pt-4 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
                <div>© {new Date().getFullYear()} Caredevo</div>
           
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* =========================
     PROGRESS TAB LOGIC
  ========================= */
  newProgress = () => {
    this.setState({
      selectedProgressId: null,
      progressText: "",
      progressMode: "new",
      progressDraftText: "",
    });
  };

  selectProgress = (entryId) => {
    const entry = this.state.progressEntries.find((p) => p.id === entryId);
    if (!entry) return;
    this.setState({
      selectedProgressId: entryId,
      progressText: entry.text || "",
      progressMode: "view",
      progressDraftText: "",
    });
  };

  submitNewProgress = () => {
    const text = (this.state.progressText || "").trim();
    if (!text) return;

    const entry = {
      id: this.makeId("p"),
      dateISO: this.todayISO(),
      text,
    };

    this.setState((prev) => ({
      progressEntries: [entry, ...prev.progressEntries],
      selectedProgressId: entry.id,
      progressMode: "view",
      progressDraftText: "",
    }));
  };

  askDeleteProgress = async (entryId) => {
    const res = await MySwal.fire({
      title: "Delete this progress note?",
      text: "This can’t be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!res.isConfirmed) return;

    this.setState((prev) => {
      const next = prev.progressEntries.filter((p) => p.id !== entryId);
      const isSelected = prev.selectedProgressId === entryId;

      return {
        progressEntries: next,
        selectedProgressId: isSelected ? null : prev.selectedProgressId,
        progressText: isSelected ? "" : prev.progressText,
        progressMode: isSelected ? "new" : prev.progressMode,
        progressDraftText: "",
      };
    });
  };

  startEditProgress = () => {
    const { selectedProgressId, progressText } = this.state;
    if (!selectedProgressId) return;
    this.setState({
      progressMode: "edit",
      progressDraftText: progressText, // store original for cancel
    });
  };

  cancelEditProgress = () => {
    const { progressDraftText } = this.state;
    // revert back to stored original
    this.setState({
      progressText: progressDraftText || "",
      progressMode: "view",
      progressDraftText: "",
    });
  };

  updateProgress = () => {
    const { selectedProgressId, progressText } = this.state;
    if (!selectedProgressId) return;

    const nextText = (progressText || "").trim();
    if (!nextText) return;

    this.setState((prev) => ({
      progressEntries: prev.progressEntries.map((p) =>
        p.id === selectedProgressId ? { ...p, text: nextText } : p
      ),
      progressMode: "view",
      progressDraftText: "",
    }));
  };

  renderProgressListItem(p) {
    const { selectedProgressId } = this.state;
    const isActive = selectedProgressId === p.id;

    const preview =
      (p.text || "").trim().slice(0, 80) +
      ((p.text || "").trim().length > 80 ? "…" : "");

    return (
      <div
        key={p.id}
        className={[
          "rounded-xl border p-3 transition cursor-pointer",
          isActive
            ? "border-sky-300 bg-sky-50"
            : "border-slate-200 bg-white hover:bg-slate-50",
        ].join(" ")}
        onClick={() => this.selectProgress(p.id)}
        role="button"
        tabIndex={0}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-xs font-semibold text-slate-500">
              {this.formatDDMMMYYYY(p.dateISO)}
            </div>
            <div className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">
              {preview || <span className="text-slate-400">—</span>}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                this.selectProgress(p.id);
              }}
              className="rounded-md p-1 hover:bg-slate-200"
              title="Edit"
            >
              <PencilSquareIcon className="h-5 w-5 text-slate-700" />
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                this.askDeleteProgress(p.id);
              }}
              className="rounded-md p-1 hover:bg-slate-200"
              title="Delete"
            >
              <TrashIcon className="h-5 w-5 text-slate-700" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderProgressActions() {
    const { selectedProgressId, progressMode } = this.state;

    // New entry mode
    if (!selectedProgressId || progressMode === "new") {
      return (
        <button
          type="button"
          onClick={this.submitNewProgress}
          className="rounded-lg bg-sky-700 px-4 py-2 text-white text-sm font-semibold hover:bg-sky-600 transition"
        >
          Submit
        </button>
      );
    }

    // View mode: show Edit + Delete
    if (progressMode === "view") {
      return (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={this.startEditProgress}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-white text-sm font-semibold hover:bg-slate-800 transition"
          >
            <PencilSquareIcon className="h-5 w-5" />
            Edit
          </button>

          <button
            type="button"
            onClick={() => this.askDeleteProgress(selectedProgressId)}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2 text-slate-800 text-sm font-semibold hover:bg-slate-200 transition"
          >
            <TrashIcon className="h-5 w-5" />
            Delete
          </button>
        </div>
      );
    }

    // Edit mode: show Cancel + Update
    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={this.cancelEditProgress}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2 text-slate-800 text-sm font-semibold hover:bg-slate-200 transition"
        >
          <XMarkIcon className="h-5 w-5" />
          Cancel
        </button>

        <button
          type="button"
          onClick={this.updateProgress}
          className="inline-flex items-center gap-2 rounded-lg bg-sky-700 px-4 py-2 text-white text-sm font-semibold hover:bg-sky-600 transition"
        >
          <CheckIcon className="h-5 w-5" />
          Update
        </button>
      </div>
    );
  }

  renderProgressTab() {
    const { progressEntries, selectedProgressId, progressMode, progressText } =
      this.state;

    const isReadOnly = selectedProgressId && progressMode === "view";

    return (
      <div className="grid grid-cols-2 md:grid-cols-2 gap-6 xs:grid-cols-1 sm:grid-cols-1">
        {/* LEFT: list */}
        <div className="md:col-span-1">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-lg font-bold text-slate-900">Progress</div>
              <div className="text-sm text-slate-600">
                Review notes over time.
              </div>
            </div>

            <button
              type="button"
              onClick={this.newProgress}
              className="rounded-lg bg-sky-700 px-3 py-2 text-white text-xs font-semibold hover:bg-sky-600 transition"
              title="New entry"
            >
              + New
            </button>
          </div>

          <div className="space-y-3">
            {progressEntries.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                No progress notes yet.
              </div>
            ) : (
              progressEntries.map((p) => this.renderProgressListItem(p))
            )}
          </div>
        </div>

        {/* RIGHT: editor */}
        <div className="md:col-span-2">
          <div className="rounded-2xl bg-slate-50 p-6 ring-1 ring-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  {selectedProgressId
                    ? `Entry: ${
                        this.formatDDMMMYYYY(
                          (progressEntries.find((p) => p.id === selectedProgressId) || {})
                            .dateISO
                        ) || "—"
                      }`
                    : "New progress entry"}
                </div>
                <div className="text-xs text-slate-600">
                  {selectedProgressId
                    ? progressMode === "edit"
                      ? "Editing mode"
                      : "View mode"
                    : "Write a new note and submit"}
                </div>
              </div>
            </div>

            <textarea
              value={progressText}
              onChange={(e) => this.setState({ progressText: e.target.value })}
              rows={12}
              readOnly={!!isReadOnly}
              placeholder="Write progress note here..."
              className={[
                "w-full rounded-lg border px-4 py-3 text-sm focus:outline-none focus:ring-2",
                isReadOnly
                  ? "border-slate-200 bg-slate-100 text-slate-700 focus:ring-slate-200"
                  : "border-slate-300 bg-white focus:ring-sky-500",
              ].join(" ")}
            />

            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-slate-500">
                Tip: click a date on the left to view, then Edit to update.
              </div>

              {this.renderProgressActions()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* =========================
     MAIN CONTENT SWITCH
  ========================= */
  renderMainContent() {
    const { activeTab } = this.state;
    if (activeTab === "profile") return this.renderProfileTab();
    if (activeTab === "form") return this.renderFormTab();
    if (activeTab === "action") return this.renderActionPlanTab();
    if (activeTab === "progress") return this.renderProgressTab();
    return this.renderPreviewTab();
  }

  render() {

    const { accessLoading, accessAllowed, accessError, leadEmail, pageId } = this.state;

    // Still getting access from backend
    if (accessLoading) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
          <div className="rounded-2xl bg-white p-8 shadow ring-1 ring-slate-200 max-w-md w-full">
            <div className="text-lg font-bold text-slate-900">Checking access…</div>
            <div className="mt-2 text-sm text-slate-600">
              Please wait while we verify your link.
            </div>
          </div>
        </div>
      );
    }
    
    // loading content when access allowed
    if (!accessAllowed) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
          <div className="rounded-2xl bg-white p-8 shadow ring-1 ring-slate-200 max-w-md w-full">
            <div className="text-lg font-bold text-slate-900">GPCCMP locked</div>

            <div className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">
              {accessError || "Access denied."}
            </div>

            {leadEmail ? (
              <div className="mt-3 text-xs text-slate-500">
                Email: <span className="font-semibold">{leadEmail}</span>
              </div>
            ) : null}

            <button
              type="button"
              onClick={this.checkAccessOnServer}
              className="mt-5 w-full rounded-lg bg-sky-700 px-4 py-2 text-white text-sm font-semibold hover:bg-sky-600"
            >
              Re-check access
            </button>
          </div>
        </div>
      );
    }


    return (
      <div className="min-h-screen bg-slate-50 px-6 py-10 print:px-0 print:py-0 print:bg-white">

        <div className="mx-auto max-w-5xl grid grid-cols-2 print:mx-0 print:w-full print:max-w-none">

          <div className='col-span-2 grid grid-cols-2 xs:grid-cols-1 sm:grid-cols-1'>
          <div className="mb-6 print:hidden">
            <h1 className="text-3xl font-bold text-slate-900 print:hidden">
              Mental Health Care Plan
            </h1>
            <p className="mt-2 text-slate-600 print:hidden">
              Create once. Manage the care. Update the plan.
            </p>
          </div>
          <div className="ml-auto flex gap-2 print:hidden ">
            {!this.state.hasDocOpen ? (
              <>
                <button onClick={this.handleNew} className="rounded-lg my-8 bg-sky-700 px-4 py-2 text-white text-sm font-semibold hover:bg-sky-600">
                  New
                </button>
                <button onClick={this.loadEncryptedCDV} className="rounded-lg my-8 bg-slate-900 px-4 py-2 text-white text-sm font-semibold hover:bg-slate-800">
                  Load
                </button>
              </>
            ) : (
              <>
                <button onClick={this.saveEncryptedCDV} className="rounded-lg my-8  bg-sky-700 px-4 py-2 text-white text-sm font-semibold hover:bg-sky-600">
                  Save
                </button>
                <button onClick={this.handleClose} className="rounded-lg my-8  bg-slate-900 px-4 py-2 text-white text-sm font-semibold hover:bg-slate-800">
                  Close
                </button>
              </>
            )}
          </div>
          </div>

        {!this.state.hasDocOpen ? (
          <div className="col-span-2 rounded-2xl bg-white p-10 shadow ring-1 ring-slate-200">
            <div className="text-sm text-slate-600">
              No file open. Click <span className="font-semibold">New</span> or{" "}
              <span className="font-semibold">Load</span>.
            </div>
          </div>
        ) : (
          <>
            <div className='col-span-2'>
            {this.renderMainTabs()}
            </div>
            <div className="col-span-2 rounded-2xl bg-white p-6 shadow ring-1 ring-slate-200 print:p-0 print:ring-0">
              {this.renderMainContent()}
            </div>
          </>
        )}

        </div>
        {this.state.passwordDialogOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4 print:hidden">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl ring-1 ring-slate-200">
              <div className="text-lg font-bold text-slate-900">
                {this.state.passwordDialogTitle}
              </div>
              {this.state.passwordDialogMessage ? (
                <div className="mt-1 text-sm text-slate-600">
                  {this.state.passwordDialogMessage}
                </div>
              ) : null}

              <input
                ref={this.pwInputRef}
                type="password"
                value={this.state.passwordDialogValue}
                onChange={(e) =>
                  this.setState({ passwordDialogValue: e.target.value, passwordDialogError: "" })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") this._confirmPasswordDialog?.();
                  if (e.key === "Escape") this._cancelPasswordDialog?.();
                }}
                className="mt-4 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                placeholder="Password"
              />

              {this.state.passwordDialogError ? (
                <div className="mt-2 text-sm text-red-600">{this.state.passwordDialogError}</div>
              ) : null}

              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => this._cancelPasswordDialog?.()}
                  className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-200"
                >
                  {this.state.passwordDialogCancelText}
                </button>
                <button
                  type="button"
                  onClick={() => this._confirmPasswordDialog?.()}
                  className="rounded-lg bg-sky-700 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-600"
                >
                  {this.state.passwordDialogOkText}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    );
  }
}

export default MHCPTool;
